/**
 * Jubit unified SSO popup-bridge — produces Supabase session tokens.
 *
 * ADR-0099 + ADR-0100 (Sprint 30.4).
 *
 * Distinct from `jubitOAuth.ts`:
 *   - `jubitOAuth.openJubitOAuth()` is the Sprint 15 / ADR-0008 credit-bridge
 *     flow. Returns `{token, username}` (Jubit-internal credit JWT, NOT
 *     a Supabase session). Used by `bridgeStore` for credit management.
 *   - `openJubitSsoBridge()` (this file) is the unified-Supabase-session
 *     bridge. Returns `{access_token, refresh_token, expires_at}` that the
 *     caller feeds to `supabase.auth.setSession()`. Used to span eTLD
 *     boundaries (jubuddy.com / gghere.com / dseek.ai → jubit.ai cookie).
 *
 * Responder contract (jubit-ai-universe ships this; see ADR-0100):
 *
 *   GET https://www.jubit.ai/sso/bridge?app=<id>&state=<csrf>&return=<origin>
 *
 *   Behaviour on the responder side:
 *     1. If `return` not in jubit-ai-universe's allowlist → render error
 *     2. If user already authed on jubit.ai → immediately postMessage tokens
 *     3. Else trigger jubit.ai sign-in flow, then postMessage on success
 *     4. postMessage payload:
 *        { kind: 'jubit-sso-bridge',
 *          state: <echo>,
 *          access_token: <supabase JWT>,
 *          refresh_token: <opaque>,
 *          expires_at?: <epoch seconds> }
 *     5. postMessage targetOrigin: MUST equal `return` exactly (no `*`)
 *     6. Auto-close popup after postMessage
 *
 * Security invariants enforced here:
 *   - event.origin must equal VITE_JUBIT_ORIGIN exactly (no regex)
 *   - payload.state must equal the CSRF state generated at popup open
 *   - access_token JWT-shaped; refresh_token GoTrue-aligned (legacy 12 [a-z0-9] or longer opaque)
 *   - 5-minute timeout; popup-closed detection within 500 ms +
 *     ~300 ms grace so an in-flight postMessage can still win (S+3)
 *
 * The responder is live at https://www.jubit.ai/sso/bridge, so the bridge is ON by
 * default. `VITE_JUBIT_USE_SSO_BRIDGE === 'false'` is the kill switch — see
 * shouldUseSsoBridge for why the polarity is opt-out rather than opt-in.
 */

export interface JubitSsoBridgeResult {
  /** Supabase access token (JWT). Pass to `supabase.auth.setSession`. */
  accessToken: string;
  /** Supabase refresh token (opaque). Pass to `supabase.auth.setSession`. */
  refreshToken: string;
  /** Unix epoch seconds. Optional — Supabase derives from JWT if absent. */
  expiresAt?: number;
}

export type JubitSsoBridgeErrorCode =
  | 'popup-blocked'
  | 'cancelled'
  | 'origin-mismatch'
  | 'state-mismatch'
  | 'malformed-payload'
  | 'timeout'
  | 'bridge-not-configured'
  // Spoke setSession rejected the handoff tokens — surface; never silent OAuth.
  | 'set-session-failed';

/** Safe attribution only — never token values or substrings (S+4.B). */
export type JubitSsoBridgeErrorDetails = {
  readonly bridgeUrl?: string;
  readonly state?: string;
  readonly jubitOrigin?: string;
  /** access_token passed looksLikeJwt */
  readonly atOk?: boolean;
  /** access_token `.`-segment count (0 if non-string) */
  readonly atSeg?: number;
  /** typeof refresh_token (or "null") */
  readonly rtType?: string;
  /** refresh_token length, or -1 if non-string */
  readonly rtLen?: number;
};

export class JubitSsoBridgeError extends Error {
  constructor(
    public readonly code: JubitSsoBridgeErrorCode,
    message?: string,
    public readonly details?: JubitSsoBridgeErrorDetails,
  ) {
    super(message ?? code);
    this.name = 'JubitSsoBridgeError';
  }
}

interface ResolvedConfig {
  bridgeUrl: string;
  jubitOrigin: string;
}

/**
 * Read the env vars needed to open the bridge. The bridge URL is derived
 * from `VITE_JUBIT_ORIGIN` (e.g. `https://www.jubit.ai/sso/bridge`) so
 * spoke apps don't need to track a third env var.
 */
function resolveConfig(env: Record<string, string | undefined> = readEnv()): ResolvedConfig | null {
  const jubitOrigin = env.VITE_JUBIT_ORIGIN;
  if (!jubitOrigin) return null;
  // Strip trailing slash for a clean URL join.
  const normalisedOrigin = jubitOrigin.replace(/\/+$/, '');
  return {
    bridgeUrl: `${normalisedOrigin}/sso/bridge`,
    jubitOrigin: normalisedOrigin,
  };
}

function readEnv(): Record<string, string | undefined> {
  try {
    // Only the keys this module reads, each by name: returning `import.meta.env`
    // itself made Vite inline the whole env object (every VITE_* var, Vercel's
    // commit metadata included) into the entry chunk.
    return {
      VITE_JUBIT_ORIGIN: import.meta.env.VITE_JUBIT_ORIGIN,
      VITE_JUBIT_USE_SSO_BRIDGE: import.meta.env.VITE_JUBIT_USE_SSO_BRIDGE,
    };
  } catch {
    return {};
  }
}

/**
 * Returns true iff the unified SSO bridge should be used for the current
 * host. Logic:
 *   - NOT disabled via `VITE_JUBIT_USE_SSO_BRIDGE === 'false'`, AND
 *   - Current host is NOT a `*.jubit.ai` subdomain (those already share
 *     the apex cookie via `cookieDomain: '.jubit.ai'`).
 *
 * Default ON, matching dseek's copy of this module.
 *
 * It was opt-in while the paired responder was still unbuilt. That responder has
 * since shipped and is live at www.jubit.ai/sso/bridge, but the flag was never set
 * in jubuddy's Vercel environment — so the bridge compiled to `undefined !== 'true'`
 * and was dead in production for months, while the code read as though it worked.
 * A default that works in production cannot fail that way; the env var remains as a
 * kill switch.
 */
export function shouldUseSsoBridge(
  host: string = typeof window !== 'undefined' ? window.location.hostname : '',
  env: Record<string, string | undefined> = readEnv(),
): boolean {
  if (env.VITE_JUBIT_USE_SSO_BRIDGE === 'false') return false;
  // *.jubit.ai already share cookies; the bridge is a noop there.
  if (/\.jubit\.ai$/i.test(host) || host === 'jubit.ai') return false;
  return true;
}

/**
 * The `app=` label the responder themes its page with. It is cosmetic — identity
 * is one Supabase project and the uuid is the same either way — but a visitor who
 * clicked "Sign in" on gghere.com was shown a popup branded **jubuddy**, a product
 * name that appears nowhere on the site they came from. Say the host's own name.
 *
 * Only the gghere apex + its subdomains rename; jubuddy.com, play.jubit.ai, preview
 * deploys and localhost all stay 'jubuddy', which is the canonical app id.
 */
export function ssoAppLabel(
  host: string = typeof window !== 'undefined' ? window.location.hostname : '',
): string {
  return /(^|\.)gghere\.com$/i.test(host) ? 'gghere' : 'jubuddy';
}

/** 32-hex CSRF state. Mirrors `jubitOAuth.generateState`. */
function generateState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Cheap shape check — Supabase JWTs are 3 base64url segments. */
function looksLikeJwt(s: unknown): s is string {
  if (typeof s !== 'string') return false;
  const parts = s.split('.');
  return parts.length === 3 && parts.every((p) => p.length > 0);
}

/** GoTrue-aligned RT check (supabase/auth internal/api/token_refresh.go):
 * legacy refresh tokens are exactly 12 [a-z0-9]; v2 tokens are longer opaque.
 * Do not require >=16 — that false-negatives valid password sessions (S+7).
 */
function looksLikeRefreshToken(s: unknown): s is string {
  if (typeof s !== 'string') return false;
  if (s.length < 12) return false;
  if (s.length === 12) return /^[a-z0-9]{12}$/.test(s);
  return true;
}

/** Shape breadcrumbs for malformed-payload — lengths/types only, never token values (S+4.B). */
function malformedPayloadDetails(
  accessToken: unknown,
  refreshToken: unknown,
): Pick<JubitSsoBridgeErrorDetails, 'atOk' | 'atSeg' | 'rtType' | 'rtLen'> {
  const atOk = looksLikeJwt(accessToken);
  const atSeg = typeof accessToken === 'string' ? accessToken.split('.').length : 0;
  const rtType = refreshToken === null ? 'null' : typeof refreshToken;
  const rtLen = typeof refreshToken === 'string' ? refreshToken.length : -1;
  return { atOk, atSeg, rtType, rtLen };
}

const POPUP_FEATURES = 'popup=yes,width=480,height=640,scrollbars=yes,resizable=no';
const TIMEOUT_MS = 5 * 60 * 1000;
/** After popup.closed, wait this long before reject cancelled (S+3). */
const CLOSE_GRACE_MS = 300;
/** Exported so an opener can re-focus a popup it is still waiting on: re-opening
 *  by NAME with an empty URL returns the same window without navigating it. */
export const SSO_POPUP_NAME = 'jubit-sso-bridge';
const POPUP_NAME = SSO_POPUP_NAME;

export interface SsoBridgeAttempt {
  readonly url: string;
  readonly state: string;
  readonly jubitOrigin: string;
}

export interface OpenBridgeOptions {
  /** Spoke app identifier passed to the responder so it can theme the page. */
  app?: string;
  /**
   * Override `window.open` for tests. Returns a popup handle (with `closed`
   * + `close()` props) or `null` to simulate popup-blocked.
   */
  openWindow?: (url: string, target: string, features: string) => Window | null;
  /** Override `window.addEventListener('message', ...)` for tests. */
  addMessageListener?: (handler: (event: MessageEvent) => void) => () => void;
  /** Override the env reader for tests. */
  env?: Record<string, string | undefined>;
}

/**
 * Build the responder URL + CSRF state without opening a window.
 * Used by the popup-blocked fallback (ADR-0330 R-3) so a later
 * user-gesture click can open the same URL.
 */
export function createSsoBridgeAttempt(options: OpenBridgeOptions = {}): SsoBridgeAttempt | null {
  const config = resolveConfig(options.env);
  if (!config) return null;
  const state = generateState();
  const url = new URL(config.bridgeUrl);
  url.searchParams.set('app', options.app ?? ssoAppLabel());
  url.searchParams.set('state', state);
  const returnOrigin =
    typeof window !== 'undefined' ? window.location.origin : 'https://gghere.com';
  url.searchParams.set('return', returnOrigin);
  return { url: url.toString(), state, jubitOrigin: config.jubitOrigin };
}

export interface WaitForSsoBridgeOptions {
  readonly state: string;
  readonly jubitOrigin: string;
  readonly popup?: Window | null;
  addMessageListener?: (handler: (event: MessageEvent) => void) => () => void;
}

/**
 * Wait for the responder postMessage. If `popup` is set, closing it
 * cancels. If omitted (popup-blocked fallback), only message + timeout.
 */
export function waitForSsoBridgeResult(
  options: WaitForSsoBridgeOptions,
): Promise<JubitSsoBridgeResult> {
  const { state, jubitOrigin, popup } = options;

  return new Promise<JubitSsoBridgeResult>((resolve, reject) => {
    let cleanedUp = false;
    let unsubscribeMessage: (() => void) | null = null;
    let closeGraceHandle: ReturnType<typeof setTimeout> | null = null;
    // Cleanup runs only after both timers have been installed.

    function cleanup() {
      if (cleanedUp) return;
      cleanedUp = true;
      if (unsubscribeMessage) unsubscribeMessage();
      clearInterval(closedPoll);
      clearTimeout(timeoutHandle);
      if (closeGraceHandle) {
        clearTimeout(closeGraceHandle);
        closeGraceHandle = null;
      }
      try {
        if (popup && !popup.closed) popup.close();
      } catch {
        // Cross-origin close can throw in some browsers — ignore.
      }
    }

    function onMessage(event: MessageEvent) {
      if (event.origin !== jubitOrigin) return;
      const data = event.data as Partial<{
        kind: string;
        state: string;
        access_token: string;
        refresh_token: string;
        expires_at: number;
        error: string;
      }> | null;
      if (!data || data.kind !== 'jubit-sso-bridge') return;
      if (data.error) {
        cleanup();
        reject(new JubitSsoBridgeError('cancelled', data.error));
        return;
      }
      if (data.state !== state) {
        cleanup();
        reject(new JubitSsoBridgeError('state-mismatch', 'CSRF state mismatch'));
        return;
      }
      if (!looksLikeJwt(data.access_token) || !looksLikeRefreshToken(data.refresh_token)) {
        cleanup();
        const details = malformedPayloadDetails(data.access_token, data.refresh_token);
        // Safe breadcrumbs only — never token values (S+4.B).
        console.warn('[sso-bridge]', 'malformed-payload', details);
        reject(new JubitSsoBridgeError('malformed-payload', 'malformed-payload', details));
        return;
      }
      cleanup();
      resolve({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt:
          typeof data.expires_at === 'number' && Number.isFinite(data.expires_at)
            ? data.expires_at
            : undefined,
      });
    }

    const closedPoll = setInterval(() => {
      if (!popup) return;
      try {
        if (popup.closed) {
          // S+3: stop polling, but give an in-flight postMessage a short
          // window to win before we reject cancelled (hub closes ~1.5s
          // after post; race was false-cancel before message handled).
          clearInterval(closedPoll);
          if (closeGraceHandle || cleanedUp) return;
          closeGraceHandle = setTimeout(() => {
            closeGraceHandle = null;
            if (cleanedUp) return;
            cleanup();
            reject(new JubitSsoBridgeError('cancelled', 'user closed popup'));
          }, CLOSE_GRACE_MS);
        }
      } catch {
        // popup access can throw cross-origin in rare browsers — keep polling.
      }
    }, 500);

    const timeoutHandle = setTimeout(() => {
      cleanup();
      reject(new JubitSsoBridgeError('timeout'));
    }, TIMEOUT_MS);

    if (options.addMessageListener) {
      unsubscribeMessage = options.addMessageListener(onMessage);
    } else {
      window.addEventListener('message', onMessage);
      unsubscribeMessage = () => window.removeEventListener('message', onMessage);
    }
  });
}

/**
 * Opens the SSO bridge popup and resolves with Supabase session tokens.
 *
 * Caller pattern:
 *
 * ```ts
 * try {
 *   const { accessToken, refreshToken } = await openJubitSsoBridge();
 *   await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
 *   await userStore.refresh();
 * } catch (err) {
 *   if (err instanceof JubitSsoBridgeError && err.code === 'cancelled') return;
 *   throw err;
 * }
 * ```
 */
export function openJubitSsoBridge(
  options: OpenBridgeOptions = {},
): Promise<JubitSsoBridgeResult> {
  const attempt = createSsoBridgeAttempt(options);
  if (!attempt) {
    return Promise.reject(
      new JubitSsoBridgeError('bridge-not-configured', 'VITE_JUBIT_ORIGIN unset'),
    );
  }

  const opener = options.openWindow ?? window.open.bind(window);
  const popup = opener(attempt.url, POPUP_NAME, POPUP_FEATURES);
  if (!popup) {
    return Promise.reject(
      new JubitSsoBridgeError('popup-blocked', 'popup-blocked', {
        bridgeUrl: attempt.url,
        state: attempt.state,
        jubitOrigin: attempt.jubitOrigin,
      }),
    );
  }

  return waitForSsoBridgeResult({
    state: attempt.state,
    jubitOrigin: attempt.jubitOrigin,
    popup,
    addMessageListener: options.addMessageListener,
  });
}
